import {useEffect} from "react";

import {khanFetch} from "@khan/data-fetch";
import Log from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";

// The rate at which the real-time-messaging endpoint is polled. Note that
// message retrievals on multiple channels are multiplexed over a single HTTP
// request, so this is the max rate requests will be sent from a single
// client.
const defaultPollIntervalInMilliseconds = 15000;

// If there's an error polling, the poll interval is increased up to this
// maximum.
const maxPollIntervalInMilliseconds = 5 * 60000; // 5 minutes

type PollingResponse = PollingResponseMessages | PollingResponseReset;

// The response sent by the real-time endpoint when there are new messages.
// All messages for the indicated channels sent since the last `since` query
// param value are returned.
interface PollingResponseMessages {
    type: "messages";
    messages: Array<RealTimeMessageEnvelope>;
    timestamp: string;
    nextPollInMilliseconds?: number;
}

// RealTimeMessageEnvelope is the thing shuttled around by the real-time
// messaging infrastructure. The payload is what's considered the "message"
// by real-time message publishers and receivers.
export interface RealTimeMessageEnvelope {
    channel: string;
    timestamp: string;
    payload: any;
}

// This is sent by the real-time endpoint when the polling call hasn't been
// made for a while an the timestamp since via the `since` query param is
// outside the expiration window (meaning the server can't guarantee that
// messages haven't been missed).
interface PollingResponseReset {
    type: "reset";
    timestamp: string;
    nextPollInMilliseconds?: number;
}

interface Listener {
    callbacks: RealTimeMessagingCallbacksWithEnvelope;
    hasConnected: boolean;
    uniqueKey: any;
}

interface RealTimeMessagingCallbacks<T> {
    onMessage: (message: T) => void;
    onReset?: () => void;
    onConnect?: () => void;
}

type RealTimeMessagingCallbacksWithEnvelope =
    RealTimeMessagingCallbacks<RealTimeMessageEnvelope>;

export class RealTimeMessagingPoller {
    listenersByChannel: Record<string, Array<Listener>>;
    // `since` is the polling cursor. It's set to the timestamp indicated in
    // the most recent polling response. Subsequent polling calls will return
    // messages newer than this value. If multiple clients poll for messages
    // for the same user and channel(s), that's okay. Each client maintains
    // its own cursor.
    //
    // A value of "0" indicates to the backend that it should return a cursor
    // corresponding to the current time. When `since` is 0, the backend
    // doesn't return any messages (since all existing messages) are older
    // than now.
    since: string;

    constructor() {
        this.listenersByChannel = {};
        this.since = "0";
    }

    addListener(
        channel: string,
        callbacks: RealTimeMessagingCallbacksWithEnvelope,
    ): () => void {
        if (!this.listenersByChannel.hasOwnProperty(channel)) {
            this.listenersByChannel[channel] = [];
        }

        // Objects values are only === when they're the exact same object
        // instance. We use this property to filter this listener out of the
        // listeners map below when the listener needs to be removed.
        const uniqueKey = {};

        this.listenersByChannel[channel].push({
            uniqueKey,
            callbacks,
            hasConnected: false,
        });

        return () => {
            this.listenersByChannel[channel] = this.listenersByChannel[
                channel
            ].filter((l) => l.uniqueKey !== uniqueKey);
        };
    }

    // This is the "public" API for starting the poller. Note, however, that
    // external code should never construct or start a poller. Instead, the
    // useRealTimeMessaging hook should be used. That's the REAL pubic API.
    async start() {
        await this._pollLoop(defaultPollIntervalInMilliseconds);
    }

    // pollLoop is a never ending event-based loop, though if there's an error
    // the poll interval is decreased exponentially (up to a limit) until
    // polling is successful again.
    async _pollLoop(pollInterval: number) {
        try {
            // On a successful poll, reset the poll interval.
            pollInterval = await this._poll();
        } catch (e: any) {
            // Polling errors are potentially very noisy, and there isn't any
            // action to be taken when polling fails. We simply try again and
            // either 1) catch up on missed messages (if the `since` cursor
            // is recent enough) or 2) re-sync data using the `reset`
            // callback. Overall health of the real-time system is monitored
            // on the backend.
            pollInterval *= 2;
        }

        if (pollInterval > maxPollIntervalInMilliseconds) {
            pollInterval = maxPollIntervalInMilliseconds;
        }

        // eslint-disable-next-line no-restricted-syntax -- this setTimeout is never cleared
        setTimeout(() => this._pollLoop(pollInterval), pollInterval);
    }

    // Polls for new messages and notifies registered channels callbacks.
    // Returns true if the poll was successful (regardless of whether there
    // were any new messages) or false if there was an error fetching
    // messages.
    async _poll(): Promise<number> {
        const channels = Object.keys(this.listenersByChannel);

        // If there are no active consumers of real-time messages, no need
        // to fetch any messages!
        //
        // TODO(marksandstrom): Update the code to only schedule a poll if there
        // are listeners (instead of looping when there are no listeners).
        if (!channels.length) {
            return defaultPollIntervalInMilliseconds;
        }

        const params = new URLSearchParams({
            channels: channels.join(","),
            since: this.since,
        });

        // Note: khanFetch throws when a non-successful status is returned.
        const resp = await khanFetch(
            "/api/internal/_notifications/real-time-messaging?" +
                params.toString(),
        );

        const data: PollingResponse = await resp.json();

        // Update the timestamp to use for the next fetch
        this.since = data.timestamp;

        switch (data.type) {
            case "messages":
                // Make sure listeners get an onConnect callback before
                // any messages.
                this._sendOnConnectToNewListeners();

                data.messages.forEach((message) => {
                    // Send each messages to the listeners on the
                    // corresponding channel. Note that "*" is not a
                    // valid channel *on* a message. "*" is used to
                    // indicate a listener that listens on all channels.
                    // Wildcard listeners are handled below.
                    _safeForEach(
                        this.listenersByChannel[message.channel],
                        (listener) => {
                            listener.callbacks.onMessage(message);
                        },
                    );
                    _safeForEach(this.listenersByChannel["*"], (listener) => {
                        listener.callbacks.onMessage(message);
                    });
                });

                break;
            case "reset":
                _forEachItemInValues(this.listenersByChannel, (listener) => {
                    // Only send a reset if the listener has been marked
                    // as connected. Else, it doesn't matter if the
                    // listener is behind -- send onConnect instead
                    // (below).
                    if (listener.hasConnected) {
                        listener.callbacks.onReset?.();
                    }
                });

                this._sendOnConnectToNewListeners();

                break;
            default:
            // Do nothing. We don't error here so old clients are backward
            // compatible when there are new response types.
        }

        return data.nextPollInMilliseconds ?? defaultPollIntervalInMilliseconds;
    }

    _sendOnConnectToNewListeners() {
        _forEachItemInValues(this.listenersByChannel, (listener) => {
            if (!listener.hasConnected) {
                listener.callbacks.onConnect?.();
                listener.hasConnected = true;
            }
        });
    }
}

function _forEachItemInValues<T>(map: Record<any, Array<T>>, fn: (T) => void) {
    Object.values(map).forEach((list) => list.forEach((v) => fn(v)));
}

function _safeForEach<T>(array: Array<T> | null | undefined, fn: (T) => void) {
    if (!array) {
        return;
    }
    array.forEach((v) => fn(v));
}

// There's one global poller. All uses of useRealTimeMessaging are multiplexed
// over a single real-time-messaging polling call to the backend.
let _globalPoller: RealTimeMessagingPoller | null = null;

function _getPoller(): RealTimeMessagingPoller {
    if (_globalPoller === null) {
        _globalPoller = new RealTimeMessagingPoller();

        // We only want to actually poll if we've running in development or
        // production. Real-time messaging is effectively disabled in all
        // other environments.
        if (
            process.env.NODE_ENV === "production" ||
            process.env.NODE_ENV === "development"
        ) {
            _globalPoller.start();
        }
    }
    return _globalPoller;
}

// useRealTimeMessaging listens for real-time messages on the given channel
// and calls the various callbacks accordingly. The type <T> is whatever type
// real-time message senders and consumers have agreed upon.
//
// If you need to listen for messages on multiple channels, use this hook
// multiple times, once per channel.
//
// IMPORTANT: The callbacks are re-registered any time the callbacks object
// changes, so the object should be memoized using useMemo.
export function useRealTimeMessaging<T>(
    channel: string,
    callbacks: RealTimeMessagingCallbacks<T>,
) {
    useEffect(() => {
        // If the user isn't logged in, real-time messaging isn't supported.
        // So do nothing.
        if (!PermissionsInfo.isLoggedIn) {
            Log.log(
                "Real-time messaging not supported for users who aren't logged in.",
            );
            return;
        }
        // The value returned by addListener removes the listener. The return
        // value is called the channel (i.e. the effect dependency) changes
        // or when the component calling this hook is unmounted.
        return _getPoller().addListener(channel, {
            ...callbacks,
            // useRealTimeMessaging only returns the payload. In general we
            // don't want to expose the messaging envelope to callers so the
            // envelope data can be changed if needed. If callers need info
            // similar to the info in the envelope when processing data, they
            // can put that info in the message payload itself.
            onMessage: (message) => callbacks.onMessage(message.payload),
        });
    }, [channel, callbacks]);
}

// useRealTimeMessagingDebugging returns messages on all channels (for the
// current user). The onMessage callback is called with message envelopes
// instead of bare message payloads so messages from various channels can be
// distinguished from one another.
//
// IMPORTANT: The callbacks are re-registered any time the callbacks object
// changes, so the object should be memoized using useMemo.
export function useRealTimeMessagingDebugging(
    callbacks: RealTimeMessagingCallbacksWithEnvelope,
) {
    useEffect(() => {
        // If the user isn't logged in, real-time messaging isn't supported.
        // So do nothing.
        if (!PermissionsInfo.isLoggedIn) {
            Log.log(
                "Real-time messaging not supported for users who aren't logged in.",
            );
            return;
        }
        // The value returned by addListener removes the listener. This
        // happens exactly once when the component is unmounted (since there
        // are no effect dependencies).
        return _getPoller().addListener("*", callbacks);
    }, [callbacks]);
}
